import {
  getMergeConnectorCapability,
  getMergeConnectorLabel,
  getNangoProviderDescription,
  getNangoProviderLabel,
  MERGE_ALLOWED_CONNECTORS,
  NANGO_ALLOWED_INTEGRATIONS,
  NANGO_CONNECT_OFFERABLE_INTEGRATIONS,
} from '@heidi/integrations/connected-apps/contracts';

import type {
  ConnectAppOption,
  ConnectAppStack,
} from '../../../contracts/projects/integrations/connectAppChips.defaults';
import { resolveProjectConnectAppOptions } from '../../../data/projects/integrations/connectAppChips.defaults';
const MERGE_ALLOWED = new Set<string>(MERGE_ALLOWED_CONNECTORS);
const NANGO_CATALOG = new Set<string>(NANGO_ALLOWED_INTEGRATIONS);
const NANGO_OFFERABLE = new Set<string>(NANGO_CONNECT_OFFERABLE_INTEGRATIONS);

/** Merge workspace slugs that name the same app as a Nango provider key. */
const MERGE_WORKSPACE_TO_NANGO: Readonly<Record<string, string>> = {
  gmail: 'google-mail',
  google_calendar: 'google-calendar',
  google_drive: 'google-drive',
  google_docs: 'google-docs',
  google_sheets: 'google-sheet',
  google_meet: 'google-meet',
  microsoft_teams: 'microsoft-teams',
  onenote: 'one-note',
  onedrive: 'one-drive',
  sharepoint: 'sharepoint-online',
};

const nangoConnectorId = (id: string): string =>
  NANGO_CATALOG.has(id) ? id : (MERGE_WORKSPACE_TO_NANGO[id] ?? id);

const connectAppLabel = (id: string, stack: ConnectAppStack): string =>
  stack === 'nango' ? getNangoProviderLabel(id) : getMergeConnectorLabel(id);

const asNangoConnectors = (
  connectors: readonly ConnectAppOption[] | readonly string[]
): ConnectAppOption[] =>
  connectors.map((entry) => {
    const id = typeof entry === 'string' ? entry : entry.id;
    const providerKey = nangoConnectorId(id);
    const option: ConnectAppOption =
      typeof entry === 'string' ? { id } : { ...entry, id };
    return providerKey === id ? option : { ...option, providerKey };
  });

export const resolveConnectAppOptions = (
  connectors: readonly ConnectAppOption[] | readonly string[],
  stack: ConnectAppStack
): ConnectAppOption[] => {
  const label = (id: string) => connectAppLabel(id, stack);
  if (stack !== 'nango') {
    return resolveProjectConnectAppOptions(connectors, stack, {
      allowed: MERGE_ALLOWED,
      label,
    });
  }

  const nangoConnectors = asNangoConnectors(connectors);
  const offerable = resolveProjectConnectAppOptions(nangoConnectors, stack, {
    allowed: NANGO_OFFERABLE,
    label,
  });
  // Every requested app was held back. Keep those rows so the card still has
  // Deny. An empty card cannot cancel the wait.
  if (nangoConnectors.length > 0 && offerable.length === 0) {
    return resolveProjectConnectAppOptions(nangoConnectors, stack, {
      allowed: NANGO_CATALOG,
      label,
    });
  }
  return offerable;
};
/**
 * Clinician-facing "what Heidi can do once this is connected" line — the same
 * copy the Connected apps settings list shows, so the in-chat prompt and the
 * settings catalog make the same promise.
 */
export const connectAppCapability = (id: string, stack: ConnectAppStack): string =>
  stack === 'nango' ? getNangoProviderDescription(id) : getMergeConnectorCapability(id);
